import { AdminEmailEditor } from '../../../components/admin/AdminEmailEditor';
import { TeamSignaturePreview } from '../accounts/TeamSignaturePanel';
import { WelcomeEmailStatus } from '../../../components/admin/WelcomeEmailStatus';
import React, { useCallback, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalDialog,
  ModalClose,
  LinearProgress,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Option,
  Select,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import { CaretDown, CaretUp, Trash } from '@phosphor-icons/react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminBoardsAPI, AdminCrmAPI } from '../../../lib/apiAdmin';
import { getApiErrorMessage } from '../../../lib/apiError';
import { LIFECYCLE_META } from '../../../components/admin/LifecycleStageBar';
import { CrmBoardStageBar } from './CrmBoardStageBar';
import { ImpersonateUserModal } from '../../../components/admin/ImpersonateUserModal';
import { MentionTextarea } from '../../../components/admin/MentionTextarea';
import {
  DetailMetric,
  DetailRow,
  DetailSection,
} from '../../../components/common/DetailDrawer';
import { StaffPicker } from '../../../components/admin/StaffPicker';
import { MailboxConnection } from '../../../components/admin/MailboxConnection';
import type { PlatformActivity, PlatformBoardStatus } from '../../../types/admin';
import { AssignContactTask } from './AssignContactTask';
import { CrmTemplatesModal } from './CrmTemplatesModal';
import { CrmTemplatePicker } from './CrmTemplatePicker';
import { useDraftGuard } from './useDraftGuard';
import { ProfileDrafts } from '../organizations/ProfileDrafts';
import type { DraftState } from '../organizations/ProfileDrafts';
import { ConfirmDeleteDialog } from '../../../components/common/ConfirmDeleteDialog';
import { CONTACT_TIME_ZONES, timeZoneLabel } from './contactTimeZoneUtils';
import { ContactTimeZone } from './ContactTimeZone';
import { TrialAccountAction } from './TrialAccountAction';

const KIND_LABEL: Record<string, string> = {
  note: 'Note',
  call: 'Call',
  meeting: 'Meeting',
  other: 'Other',
  email: 'Email',
  email_automated: 'Automated email',
  stage_change: 'Stage',
  assignment: 'Assignment',
  inbound_form: 'Website form',
  support_ticket: 'Support ticket',
  nps_response: 'NPS',
};

const FILTERS = [
  { key: '', label: 'Everything' },
  { key: 'notes', label: 'Notes' },
  { key: 'calls', label: 'Calls' },
  { key: 'emails', label: 'Email' },
  { key: 'support', label: 'Tickets' },
  { key: 'other', label: 'Other' },
];

const when = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/**
 * Everything known about one contact, in one place: their stage, the whole
 * communication history, and their support tickets.
 */
export const LeadDetailDrawer: React.FC<{ leadId: string | null; onClose: () => void }> = ({
  leadId,
  onClose,
}) => <LeadProfile key={leadId} leadId={leadId} onClose={onClose} />;

const LeadProfile: React.FC<{ leadId: string | null; onClose: () => void }> = ({
  leadId,
  onClose,
}) => {
  const queryClient = useQueryClient();
  const [detailsExpanded, setDetailsExpanded] = useState(() => window.innerWidth >= 900);
  const [tab, setTab] = useState('activity');
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [childDrafts, setChildDrafts] = useState<Record<string, DraftState>>({});
  const reportDraft = useCallback((key: string, state: DraftState) => setChildDrafts(previous => ({ ...previous, [key]: state })), []);
  const [filter, setFilter] = useState('');
  const [noteBody, setNoteBody] = useState('');
  const [noteKind, setNoteKind] = useState<'note' | 'call' | 'meeting' | 'email' | 'other' | 'task'>('note');
  const [direction, setDirection] = useState<'outbound' | 'inbound'>('outbound');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [emailHtml, setEmailHtml] = useState('');
  const [emailTo, setEmailTo] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [exitStage, setExitStage] = useState<PlatformBoardStatus | null>(null);
  const [stageReason, setStageReason] = useState('');
  const [impersonating, setImpersonating] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});

  const open = leadId !== null;

  const leadQuery = useQuery({
    queryKey: ['admin', 'lead', leadId],
    queryFn: () => AdminCrmAPI.getLead(leadId as string),
    refetchInterval: 60_000,
    enabled: open,
  });

  const boardId = leadQuery.data?.board;
  const boardQuery = useQuery({
    queryKey: ['admin', 'board', boardId],
    queryFn: () => AdminBoardsAPI.get(boardId as number),
    enabled: open && boardId != null,
    refetchInterval: 60_000,
  });
  const defaultBoardsQuery = useQuery({
    queryKey: ['admin', 'boards', 'crm'],
    queryFn: () => AdminBoardsAPI.list('crm'),
    enabled: open && !!leadQuery.data && boardId == null,
    refetchInterval: 60_000,
  });
  const board = boardId != null ? boardQuery.data : (
    defaultBoardsQuery.data?.results.find(row => row.is_default) ?? defaultBoardsQuery.data?.results[0]
  );
  const columns = [...(board?.statuses ?? [])].sort((a, b) => a.order - b.order || a.id - b.id);
  const boardState = boardId != null ? boardQuery : defaultBoardsQuery;

  const timelineQuery = useInfiniteQuery({
    queryKey: ['admin', 'lead', leadId, 'timeline', filter],
    initialPageParam: 1,
    queryFn: ({ pageParam }) => AdminCrmAPI.getTimeline(leadId as string, filter || undefined, pageParam),
    getNextPageParam: (lastPage, _pages, lastPageParam) => lastPage.next ? lastPageParam + 1 : undefined,
    refetchInterval: 60_000,
    enabled: open,
  });

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'lead', leadId] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] });
  };

  const logMutation = useMutation({
    mutationFn: () =>
      AdminCrmAPI.logActivity(leadId as string, {
        kind: noteKind,
        summary: noteBody.split('\n')[0].slice(0, 120) || KIND_LABEL[noteKind],
        body: noteBody,
        ...(noteKind === 'email' ? { direction } : {}),
      }),
    onSuccess: () => {
      setNoteBody('');
      refresh();
    },
  });

  const updateMutation = useMutation({
    mutationFn: (changes: Record<string, unknown>) =>
      AdminCrmAPI.updateLead(leadId as string, changes),
    onSuccess: () => {
      setEditing(false);
      setDraft({});
      refresh();
    },
  });

  const deleteActivityMutation = useMutation({
    mutationFn: (activityId: string) => AdminCrmAPI.deleteActivity(activityId),
    onSuccess: refresh,
  });

  const deleteContactMutation = useMutation({
    mutationFn: (id: string) => AdminCrmAPI.deleteLead(id),
    onSuccess: (_, id) => {
      setConfirmDelete(false);
      onClose();
      queryClient.removeQueries({ queryKey: ['admin', 'lead', id] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'leads'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'task-queue'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'customers'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'customer'] });
    },
  });

  const stageMutation = useMutation({
    mutationFn: ({ column, reason }: { column: PlatformBoardStatus; reason?: string }) =>
      AdminCrmAPI.moveLead(leadId as string, column.id, 0, reason),
    onSuccess: () => {
      setExitStage(null);
      setStageReason('');
      refresh();
    },
  });

  const moveStage = (column: PlatformBoardStatus) => {
    if (stageMutation.isPending || column.id === leadQuery.data?.board_status) return;
    stageMutation.reset();
    if (['lost', 'churned'].includes(column.lifecycle_stage)) {
      setStageReason('');
      setExitStage(column);
    } else {
      stageMutation.mutate({ column });
    }
  };

  const emailMutation = useMutation({
    mutationFn: () =>
      AdminCrmAPI.sendEmail(
        leadId as string,
        emailSubject,
        emailBody,
        (emailTo ?? lead?.email ?? '').trim(),
        emailHtml || undefined,
      ),
    onSuccess: () => {
      setEmailSubject('');
      setEmailBody('');
      setEmailHtml('');
      setEmailTo(null);
      setSendError(null);
      refresh();
    },
    onError: (error: unknown) =>
      setSendError(getApiErrorMessage(error, 'That email could not be sent.')),
  });

  const lead = leadQuery.data;
  const rows: PlatformActivity[] = timelineQuery.data?.pages.flatMap(page => page.results) ?? [];
  const busy = updateMutation.isPending || stageMutation.isPending || emailMutation.isPending || logMutation.isPending || deleteContactMutation.isPending || Object.values(childDrafts).some(row => row.busy);
  const composerBusy = emailMutation.isPending || !!childDrafts['crm-template-preview']?.busy;
  const dirty =
    !!(emailSubject || emailBody || (emailTo !== null && emailTo !== lead?.email) || noteBody) ||
    (editing && Object.entries(draft).some(([key, value]) => value !== (lead?.[key as keyof typeof lead] ?? ''))) ||
    Object.values(childDrafts).some(row => row.dirty);
  const guard = useDraftGuard(dirty, busy);

  return (
    <>
    <Modal open={open} onClose={() => guard.request(onClose)}>
      <ModalDialog aria-labelledby="crm-profile-title" sx={{
        width: { xs: 'calc(100vw - 16px)', md: '85vw' }, height: { xs: 'calc(100dvh - 16px)', md: '85dvh' },
        maxWidth: 'none', p: 0, gap: 0, overflow: 'hidden', borderRadius: { xs: 'md', md: 'xl' },
      }}>
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2, pr: 6, borderBottom: '1px solid', borderColor: 'divider' }}>
        <ModalClose aria-label="Close contact" disabled={busy} onClick={() => guard.request(onClose)} />
        <Typography level="body-xs" sx={{ color: 'primary.plainColor', mb: 0.5 }}>CRM · Prospect profile</Typography>
        <DialogTitle id="crm-profile-title" sx={{ overflowWrap: 'anywhere' }}>{lead?.display_name ?? 'Contact'}</DialogTitle>
        <Typography level="body-sm" sx={{ overflowWrap: 'anywhere' }}>{lead ? [lead.company_name, lead.title, lead.email].filter(Boolean).join(' · ') : 'Loading contact details…'}</Typography>
        {lead && <TrialAccountAction lead={lead} onCreated={refresh} />}
      </Box>
      {leadQuery.isLoading && <LinearProgress aria-label="Loading contact" />}
          {leadQuery.isError && (
            <Alert color="danger" sx={{ m: 2 }}>Could not load this contact.<Button onClick={() => leadQuery.refetch()}>Retry</Button></Alert>
          )}
      {lead && (
        <Box sx={{ display: 'contents' }}>
          <ImpersonateUserModal open={impersonating} onClose={() => setImpersonating(false)} />
          <Sheet variant="outlined" sx={{ px: { xs: 2, md: 3 }, py: 1.5, borderRadius: 0, border: 0, borderBottom: '1px solid', borderColor: 'divider', minWidth: 0 }}>
                {boardState.isLoading && (
                  <Typography role="status">Loading board stages…</Typography>
                )}
                {boardState.isError && (
                  <Alert color="danger" endDecorator={<Button size="sm" onClick={() => boardState.refetch()}>Retry</Button>}>Board stages could not be loaded.</Alert>
                )}
                {columns.length > 0 && (
                  <CrmBoardStageBar columns={columns} currentId={lead.board_status} disabled={stageMutation.isPending} onMove={moveStage} />
                )}
                {!boardState.isLoading && !boardState.isError && columns.length === 0 && (
                  <Typography>No CRM columns are configured yet.</Typography>
                )}
            {lead.derived_stage && lead.derived_stage !== lead.lifecycle_stage && (
              // Shown rather than applied: somebody set this stage by hand, and
              // silently overruling them would be worse than disagreeing openly.
              <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 1 }}>
                Their subscription suggests {LIFECYCLE_META[lead.derived_stage].label}
                {lead.derived_stage_reason ? ` (${lead.derived_stage_reason.replace(/_/g, ' ')})` : ''}.
              </Typography>
            )}
                {stageMutation.isError && !exitStage && (
                  <Alert color="danger" sx={{ mt: 1 }}>{getApiErrorMessage(stageMutation.error, 'The stage could not be changed. Please try again.')}</Alert>
                )}
          </Sheet>

          <ProfileDrafts.Provider value={reportDraft}>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(250px, 0.8fr) minmax(0, 2fr)' }, alignContent: 'start' }}>
          <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', alignContent: 'start', gap: 2, minWidth: 0, borderRight: { md: '1px solid var(--joy-palette-divider)' }, bgcolor: 'background.level1' }}>
          <Button variant="plain" color="neutral" aria-expanded={detailsExpanded} aria-controls="crm-contact-details" onClick={() => setDetailsExpanded(value => !value)} endDecorator={detailsExpanded ? <CaretUp /> : <CaretDown />} sx={{ justifyContent: 'space-between', px: 0, fontSize: 'lg' }}>Contact details</Button>
          <Box id="crm-contact-details" sx={{ display: detailsExpanded ? 'grid' : 'none', gap: 2, minWidth: 0 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <DetailMetric label="Team size" value={lead.team_size || '—'} />
            <DetailMetric label="Last outreach" value={lead.last_outreach_at === undefined ? 'Unavailable' : lead.last_outreach_at ? new Date(lead.last_outreach_at).toLocaleDateString() : 'No recorded outreach'} />
          </Box>

          <DetailSection title="Owner">
            <StaffPicker
              value={lead.owner}
              onChange={(userId) => updateMutation.mutate({ owner: userId })}
              placeholder="Unassigned"
              disabled={updateMutation.isPending || editing}
            />
          </DetailSection>

                      {updateMutation.isError && (
                        <Alert color="danger">{getApiErrorMessage(updateMutation.error, 'Contact details could not be saved.')}</Alert>
                      )}
          {editing ? (
            <DetailSection
              title="Edit details"
              action={
                <Button size="sm" variant="plain" color="neutral" onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              }
            >
              {(
                [
                  ['first_name', 'First name'],
                  ['last_name', 'Last name'],
                  ['company_name', 'Company'],
                  ['email', 'Email'],
                  ['phone', 'Phone'],
                  ['title', 'Title'],
                ] as const
              ).map(([field, label]) => (
                <Input
                  key={field}
                  size="sm"
                  placeholder={label}
                  disabled={updateMutation.isPending}
                  slotProps={{ input: { 'aria-label': label } }}
                  value={draft[field] ?? ''}
                  onChange={(event) => setDraft({ ...draft, [field]: event.target.value })}
                />
              ))}
              <FormControl>
                <FormLabel>Time zone</FormLabel>
                <Autocomplete
                  size="sm"
                  placeholder="Unknown — select a time zone"
                  options={CONTACT_TIME_ZONES.map(option => option.value)}
                  getOptionLabel={timeZoneLabel}
                  value={draft.time_zone || null}
                  disabled={updateMutation.isPending}
                  onChange={(_, value) => setDraft({ ...draft, time_zone: value ?? '' })}
                />
              </FormControl>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="sm" aria-label="Save contact details" loading={updateMutation.isPending} onClick={() => updateMutation.mutate(draft)}>
                  Save
                </Button>
              </Box>
            </DetailSection>
          ) : (
            <Box>
              <DetailRow label="Company" value={lead.company_name} />
              {lead.organization_name && (
                <DetailRow
                  label="Customer"
                  value={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                      <Typography level="body-sm">{lead.organization_name}</Typography>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        onClick={() => setImpersonating(true)}
                      >
                        Sign in as…
                      </Button>
                    </Box>
                  }
                />
              )}
              <DetailRow label="Email" value={lead.email} />
              <DetailRow label="Phone" value={lead.phone} />
              <DetailRow label="Time zone" value={<ContactTimeZone zone={lead.time_zone} source={lead.time_zone_source} />} />
              <DetailRow label="Source" value={lead.source.replace(/_/g, ' ')} />
                          {lead.utm_source && (
                            <DetailRow label="Campaign" value={lead.utm_source} />
                          )}
              <Box sx={{ mt: 0.5 }}>
                <Button
                  size="sm"
                  variant="plain"
                  color="neutral"
                  disabled={updateMutation.isPending}
                  onClick={() => {
                    setDraft({
                      first_name: lead.first_name,
                      last_name: lead.last_name,
                      company_name: lead.company_name,
                      email: lead.email ?? '',
                      phone: lead.phone,
                      time_zone: lead.time_zone ?? '',
                      title: lead.title,
                    });
                    setEditing(true);
                  }}
                >
                  Edit details
                </Button>
              </Box>
            </Box>
          )}

          <Divider />

          <Modal open={exitStage !== null} onClose={() => {
            if (!stageMutation.isPending) setExitStage(null);
          }}>
            <ModalDialog
              aria-labelledby="move-stage-title"
              sx={{ width: 'min(480px, calc(100vw - 32px))' }}
            >
              <DialogTitle id="move-stage-title">Mark as {exitStage?.name}</DialogTitle>
              <DialogContent>
                Record why {lead.display_name} is leaving this stage.
              </DialogContent>
              <Box component="form" sx={{ display: 'grid', gap: 2 }} onSubmit={(event) => {
                event.preventDefault();
                if (exitStage && stageReason.trim() && !stageMutation.isPending) {
                  stageMutation.mutate({ column: exitStage, reason: stageReason.trim() });
                }
              }}>
                <FormControl required>
                  <FormLabel>Reason</FormLabel>
                  <Textarea
                    autoFocus
                    minRows={2}
                    value={stageReason}
                    onChange={(event) => setStageReason(event.target.value)}
                    disabled={stageMutation.isPending}
                    slotProps={{ textarea: { maxLength: 120 } }}
                  />
                </FormControl>
                {stageMutation.isError && (
                  <Alert color="danger" role="alert">
                    {getApiErrorMessage(stageMutation.error, 'The stage could not be changed. Please try again.')}
                  </Alert>
                )}
                <DialogActions>
                  <Button type="submit" disabled={!stageReason.trim()} loading={stageMutation.isPending}>
                    Mark as {exitStage?.name}
                  </Button>
                  <Button variant="plain" color="neutral" disabled={stageMutation.isPending} onClick={() => setExitStage(null)}>
                    Cancel
                  </Button>
                </DialogActions>
              </Box>
            </ModalDialog>
          </Modal>

          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Button
              variant="outlined"
              color="danger"
              startDecorator={<Trash size={18} />}
              disabled={busy}
              onClick={() => {
                deleteContactMutation.reset();
                setConfirmDelete(true);
              }}
            >
              Delete Contact
            </Button>
          </Box>
          </Box>
          </Box>
          <Box sx={{ p: { xs: 2, md: 3 }, minWidth: 0 }}>
          <Tabs value={tab} onChange={(_, value) => setTab(String(value))} sx={{ bgcolor: 'transparent', minWidth: 0 }}>
            <TabList sx={{ mb: 2 }}><Tab value="activity">Activity &amp; follow-up</Tab><Tab value="email">Send email</Tab></TabList>
            <TabPanel value="email" keepMounted sx={{ p: 0 }}>
          <DetailSection title="Send email">
            {/* Uses the same mailbox connected in Profile Settings. */}
            <MailboxConnection compact /><TeamSignaturePreview />
                          <FormControl required>
                            <FormLabel>Recipient email</FormLabel>
                            <Input
                              type="email"
                              value={emailTo ?? lead.email ?? ''}
                              disabled={composerBusy}
                              onChange={(event) => setEmailTo(event.target.value)}
                              placeholder="name@example.com"
                            />
                          </FormControl>
                          <Typography level="body-xs">Changes apply to this email only.</Typography>
            <CrmTemplatePicker leadId={lead.id} hasDraft={!!(emailSubject || emailBody)} disabled={composerBusy} onManage={() => setTemplatesOpen(true)} onApply={(preview) => { setEmailSubject(preview.subject); setEmailBody(preview.body); setEmailHtml(preview.body_html || ''); setSendError(null); emailMutation.reset(); }} />
            <Input
              size="sm"
              slotProps={{ input: { 'aria-label': 'Email subject', maxLength: 255 } }}
              disabled={composerBusy}
              placeholder="Subject"
              value={emailSubject}
              onChange={(event) => setEmailSubject(event.target.value)}
            />
            <AdminEmailEditor text={emailBody} html={emailHtml} disabled={composerBusy} includeSignature
              onChange={(html, text) => { setEmailBody(text); setEmailHtml(html); }} />
                          {emailMutation.isSuccess && (
                            <Alert color="success" role="status">Email sent. A copy is in the activity timeline.</Alert>
                          )}
            {sendError && (
              <Typography level="body-xs" color="danger">
                {sendError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                size="sm"
                              disabled={
                                composerBusy ||
                                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(
                                  (emailTo ?? lead.email ?? '').trim(),
                                ) ||
                                !emailSubject.trim() ||
                                !emailBody.trim()
                              }
                loading={emailMutation.isPending}
                onClick={() => emailMutation.mutate()}
              >
                Send
              </Button>
            </Box>
          </DetailSection>

          <Divider />
          </TabPanel>
          <TabPanel value="activity" keepMounted sx={{ p: 0, display: tab === 'activity' ? 'grid' : 'none', gap: 2 }}>
          <DetailSection title="Log activity or assign a follow-up">
            <Select
              size="sm"
              aria-label="Activity type"
              value={noteKind}
              onChange={(_, value) => value && setNoteKind(value)}
              disabled={logMutation.isPending || Object.values(childDrafts).some(row => row.busy)}
            >
              <Option value="note">Note</Option>
              <Option value="call">Call</Option>
              <Option value="meeting">Meeting</Option>
              <Option value="email">Email</Option>
              <Option value="other">Other</Option>
              <Option value="task">Assign task</Option>
            </Select>
            <Box sx={{ display: noteKind === 'task' ? 'block' : 'none' }}><AssignContactTask key={lead.id} lead={lead} /></Box>
            {noteKind !== 'task' && (
              <>
                {noteKind === 'email' && (
                  <Select size="sm" aria-label="Email direction" value={direction} onChange={(_, value) => value && setDirection(value)}>
                    <Option value="outbound">Sent</Option>
                    <Option value="inbound">Received</Option>
                  </Select>
                )}
                <MentionTextarea
                  minRows={2}
                  placeholder="What happened? @name to notify somebody"
                  value={noteBody}
                  onChange={(value) => { if (!logMutation.isPending) setNoteBody(value); }}
                />
                {logMutation.isError && (
                  <Typography color="danger" role="alert">{getApiErrorMessage(logMutation.error, 'This activity could not be saved.')}</Typography>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    size="sm"
                    variant="outlined"
                    disabled={!noteBody.trim()}
                    loading={logMutation.isPending}
                    onClick={() => logMutation.mutate()}
                  >
                    Save
                  </Button>
                </Box>
              </>
            )}
          </DetailSection>

          <Divider />

          <DetailSection title="History">
                          {deleteActivityMutation.isError && (
                            <Alert color="danger">{getApiErrorMessage(deleteActivityMutation.error, 'Activity could not be deleted.')}</Alert>
                          )}
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
              {FILTERS.map((option) => (
                <Chip
                  key={option.key}
                  size="sm"
                  variant={filter === option.key ? 'solid' : 'outlined'}
                  color={filter === option.key ? 'primary' : 'neutral'}
                  onClick={() => setFilter(option.key)}
                >
                  {option.label}
                </Chip>
              ))}
            </Box>

                          {timelineQuery.isLoading && (
                            <Typography role="status">Loading history…</Typography>
                          )}
            {timelineQuery.isError && (
              <Box role="alert">
                <Typography color="danger">History could not be loaded.</Typography>
                <Button size="sm" onClick={() => timelineQuery.isFetchNextPageError ? timelineQuery.fetchNextPage() : timelineQuery.refetch()}>Retry history</Button>
              </Box>
            )}
            {!timelineQuery.isLoading && !timelineQuery.isError && rows.length === 0 && (
              <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
                Nothing recorded yet.
              </Typography>
            )}

            <Box sx={{ display: 'grid', gap: 1 }}>
              {rows.map((row) => (
                <Sheet key={row.id} variant="soft" sx={{ p: 1.5, borderRadius: 'sm' }}>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Chip size="sm" variant="outlined" color="neutral">
                      {KIND_LABEL[row.kind] ?? row.kind}
                    </Chip>
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {when(row.occurred_at)}
                      {row.actor_name ? ` · ${row.actor_name}` : ''}
                    </Typography>
                  </Box>
                  {row.kind === 'email' && (
                    <Typography level="body-xs" sx={{ mt: 0.5, color: 'text.tertiary', overflowWrap: 'anywhere' }}>
                      {row.metadata.source === 'gmail' ? 'Gmail · ' : row.metadata.source === 'outlook' ? 'Outlook · ' : row.metadata.source === 'manual' ? 'Manually logged · ' : ''}
                      {row.metadata.direction === 'outbound' ? 'Sent' : row.metadata.direction === 'inbound' ? 'Received' : ''}
                      {typeof row.metadata.from === 'string' ? ` · From ${row.metadata.from}` : ''}
                      {Array.isArray(row.metadata.to) && row.metadata.to.length ? ` · To ${row.metadata.to.join(', ')}` : ''}
                    </Typography>
                  )}
                  <WelcomeEmailStatus metadata={row.metadata} />
                                {(!row.body?.trim() || !row.body.trim().startsWith(row.summary.trim())) && (
                                  <Typography level="body-sm" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {row.summary}
                  </Typography>
                                )}
                  {row.body && (
                    <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                      {row.body}
                    </Typography>
                  )}
                  {row.is_editable && (
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button
                        size="sm"
                        variant="plain"
                        color="neutral"
                        disabled={deleteActivityMutation.isPending}
                        onClick={() => deleteActivityMutation.mutate(row.id)}
                      >
                        Delete
                      </Button>
                    </Box>
                  )}
                  {row.support_ticket_detail && (
                    <Chip
                      size="sm"
                      variant="soft"
                      color={row.support_ticket_detail.is_open ? 'warning' : 'success'}
                      sx={{ mt: 0.75 }}
                    >
                      {row.support_ticket_detail.is_open ? 'Open' : 'Resolved'} ·{' '}
                      {row.support_ticket_detail.ticket_number}
                    </Chip>
                  )}
                </Sheet>
              ))}
            </Box>
            {timelineQuery.hasNextPage && (
              <Button variant="outlined" loading={timelineQuery.isFetchingNextPage} onClick={() => timelineQuery.fetchNextPage()}>
                Load older activity
              </Button>
            )}
          </DetailSection>

          <Divider />

          <ConfirmDeleteDialog
            open={confirmDelete}
            label="Delete Contact"
            pending={deleteContactMutation.isPending}
            error={deleteContactMutation.error}
            onClose={() => setConfirmDelete(false)}
            onConfirm={() => deleteContactMutation.mutate(lead.id)}
          >
            <Typography sx={{ overflowWrap: 'anywhere' }}>
              Permanently delete <strong>{lead.display_name}</strong>
              {lead.email ? ` (${lead.email})` : ''} from CRM?
            </Typography>
            <Typography level="body-sm">
              Their CRM history, stored CRM email copies, and linked tasks will also be
              deleted. This cannot be undone.
            </Typography>
            <Typography level="body-sm">
              Customer accounts, support tickets, and emails in Gmail or Outlook will be kept.
            </Typography>
          </ConfirmDeleteDialog>
          </TabPanel>
          </Tabs>
          </Box>
          </Box>
          </ProfileDrafts.Provider>
        </Box>
      )}
      </ModalDialog>
    </Modal>
    {guard.dialog}
    {templatesOpen && <CrmTemplatesModal onClose={() => setTemplatesOpen(false)} />}
    </>
  );
};

export default LeadDetailDrawer;
